import type {
  DemandAnalysis,
  DemandBucket,
  HeadwayBand,
  ScheduleImportRow,
} from "../../api/types";

/**
 * The arithmetic behind the Schedules page's demand view, kept free of React
 * so it can be tested on its own (see demandMath.test.ts).
 *
 * One convention runs through all of it: a bucket's time is when a trip leaves
 * its first stop, for demand and for supply alike. The server re-times every
 * rider to the departure of the trip they boarded, and a timetable row's
 * start_time is that same departure, so the two line up bucket for bucket.
 */

export type Dir = "upward" | "downward";
export const DIRS: Dir[] = ["upward", "downward"];

// ── formatting ─────────────────────────────────────────────────────────────

export function fmtHHMM(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** Whole riders with a thousands separator; sub-1 values keep one decimal. */
export function fmtRiders(n: number): string {
  if (!Number.isFinite(n)) return "–";
  if (n > 0 && n < 0.95) return n.toFixed(1);
  return Math.round(n).toLocaleString("en-US");
}

export function fmtPct(ratio: number): string {
  if (!Number.isFinite(ratio)) return "–";
  return `${Math.round(ratio * 100)}%`;
}

/** "High Court Metro Station" -> "High Court"; "NHA Station" -> "NHA". */
export function stationLabel(name: string): string {
  const trimmed = name.replace(/\s+(metro\s+)?station$/i, "").trim();
  return trimmed || name;
}

/** A label for cramped axes: initials for long multi-word names ("Faiz Ahmed Faiz" -> "FAF"). */
export function stationAbbr(name: string): string {
  const label = stationLabel(name);
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length < 2 || label.length <= 8) return label;
  return words.map((w) => w[0]).join("").toUpperCase();
}

// ── supply: what a timetable offers ────────────────────────────────────────

/** "06:05" or "06:05:00" to minutes after midnight; null when it is neither. */
export function hhmmToMin(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec((hhmm ?? "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 47 || min > 59) return null;
  return (h % 24) * 60 + min;
}

export type TripCounts = Map<number, Record<Dir, number>>;

/**
 * Trips leaving their first stop in each bucket, by direction. Rows that are
 * not revenue trips (a blank or errored row, a deadhead) are not capacity and
 * are left out.
 */
export function tripsPerBucket(rows: ScheduleImportRow[], bucketMin: number): TripCounts {
  const out: TripCounts = new Map();
  for (const row of rows) {
    if (row.error) continue;
    const state = (row.operational_state ?? "").toLowerCase();
    if (state && state !== "operational") continue;
    const start = hhmmToMin(row.start_time);
    if (start == null) continue;
    const dir = (row.direction ?? "").toLowerCase();
    if (dir !== "upward" && dir !== "downward") continue;
    const key = Math.floor(start / bucketMin) * bucketMin;
    const cell = out.get(key) ?? { upward: 0, downward: 0 };
    cell[dir] += 1;
    out.set(key, cell);
  }
  return out;
}

// ── fit: demand against supply, bucket by bucket ───────────────────────────

export interface FitSide {
  /** Riders on the busiest link, for trips leaving in the bucket. */
  peak: number;
  peakLink: number;
  boardings: number;
  trips: number;
  /** trips × bus capacity. */
  capacity: number;
  /** peak / capacity; null when no trip runs (nothing to divide by). */
  loadFactor: number | null;
}

export interface FitRow {
  startMin: number;
  upward: FitSide;
  downward: FitSide;
}

export function fitRows(buckets: DemandBucket[], trips: TripCounts | null, busCapacity: number): FitRow[] {
  return buckets.map((b) => {
    const side = (dir: Dir): FitSide => {
      const d = b[dir];
      const n = trips?.get(b.start_min)?.[dir] ?? 0;
      const capacity = n * busCapacity;
      return {
        peak: d.peak_load,
        peakLink: d.peak_link,
        boardings: d.boardings,
        trips: n,
        capacity,
        loadFactor: capacity > 0 ? d.peak_load / capacity : null,
      };
    };
    return { startMin: b.start_min, upward: side("upward"), downward: side("downward") };
  });
}

// ── planning: the headway a level of demand needs ──────────────────────────

export interface PlanParams {
  /** Riders a bus carries, seated and standing. */
  busCapacity: number;
  /** Share of that capacity to plan to, 0–1. Leaves room for a busy day. */
  targetLoad: number;
  /** The tightest headway the operation can run, in minutes. */
  minHeadway: number;
  /** The widest gap riders should ever be left with, in minutes. */
  maxHeadway: number;
}

export const DEFAULT_PLAN: PlanParams = {
  busCapacity: 70,
  targetLoad: 0.85,
  minHeadway: 2,
  maxHeadway: 15,
};

/**
 * Headways a timetable is written in. Every minute up to ten, where a minute
 * is a real difference in buses, then the clock-face values riders can
 * remember.
 */
const HEADWAY_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 30, 60];

export interface HeadwayNeed {
  headway: number;
  /** Trips per hour in each direction that the load requires (not rounded). */
  tripsNeeded: number;
  /** Load against capacity once that headway is run. */
  loadFactor: number;
  /** True when even the minimum headway leaves the load above target. */
  overMinimum: boolean;
}

/**
 * The widest headway that still carries `ridersPerHour` past the busiest
 * point at the target load, within the operation's limits.
 */
export function headwayFor(ridersPerHour: number, p: PlanParams): HeadwayNeed {
  const perTrip = Math.max(1, p.busCapacity * p.targetLoad);
  const tripsNeeded = ridersPerHour / perTrip;
  const lo = Math.max(1, Math.min(p.minHeadway, p.maxHeadway));
  const hi = Math.max(lo, p.maxHeadway);
  const allowed = HEADWAY_STEPS.filter((h) => h >= lo && h <= hi);
  if (allowed.length === 0) allowed.push(lo);

  // Widest allowed headway whose trips per hour still cover the need.
  let headway = allowed[0];
  for (const h of allowed) {
    if (60 / h >= tripsNeeded) headway = h;
  }
  const capacityPerHour = (60 / headway) * p.busCapacity;
  return {
    headway,
    tripsNeeded,
    loadFactor: capacityPerHour > 0 ? ridersPerHour / capacityPerHour : 0,
    overMinimum: 60 / headway < tripsNeeded,
  };
}

/** Riders per hour past the busiest point, worse direction, for each clock hour. */
export function hourlyDesignLoad(buckets: DemandBucket[], bucketMin: number): Map<number, number> {
  const perHour = 60 / bucketMin;
  const out = new Map<number, number>();
  for (const b of buckets) {
    const hour = Math.floor(b.start_min / 60) * 60;
    // With sub-hour buckets the hour is sized for its busiest slice, scaled
    // to an hourly rate: a half hour carrying 600 needs the buses of 1,200/h.
    const rate = Math.max(b.upward.peak_load, b.downward.peak_load) * perHour;
    out.set(hour, Math.max(out.get(hour) ?? 0, rate));
  }
  return out;
}

export interface SuggestedBand extends HeadwayBand {
  /** Design load for the band: the busiest hour inside it, riders per hour. */
  ridersPerHour: number;
  loadFactor: number;
  overMinimum: boolean;
}

/**
 * Headway bands covering [serviceStart, serviceEnd) exactly, which is what the
 * generator requires. Boundaries fall on the hour; neighbouring hours that
 * need the same headway are merged into one band.
 */
export function suggestBands(
  buckets: DemandBucket[],
  bucketMin: number,
  serviceStartMin: number,
  serviceEndMin: number,
  p: PlanParams
): SuggestedBand[] {
  if (serviceEndMin <= serviceStartMin) return [];
  const load = hourlyDesignLoad(buckets, bucketMin);

  const bands: SuggestedBand[] = [];
  let from = serviceStartMin;
  while (from < serviceEndMin) {
    const hour = Math.floor(from / 60) * 60;
    const to = Math.min(hour + 60, serviceEndMin);
    const ridersPerHour = load.get(hour) ?? 0;
    const need = headwayFor(ridersPerHour, p);
    const last = bands[bands.length - 1];
    if (last && last.headway_min === need.headway) {
      last.to_min = to;
      if (ridersPerHour > last.ridersPerHour) {
        last.ridersPerHour = ridersPerHour;
        last.loadFactor = need.loadFactor;
        last.overMinimum = need.overMinimum;
      }
    } else {
      bands.push({
        from_min: from,
        to_min: to,
        headway_min: need.headway,
        ridersPerHour,
        loadFactor: need.loadFactor,
        overMinimum: need.overMinimum,
      });
    }
    from = to;
  }
  return bands;
}

/** Buses needed in service to hold `headway` on a route with this round trip. */
export function fleetFor(headwayMin: number, roundTripMin: number): number {
  if (headwayMin <= 0 || roundTripMin <= 0) return 0;
  return Math.ceil(roundTripMin / headwayMin);
}

export function sameBands(a: HeadwayBand[], b: HeadwayBand[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (x, i) => x.from_min === b[i].from_min && x.to_min === b[i].to_min && x.headway_min === b[i].headway_min
  );
}

// ── time windows ───────────────────────────────────────────────────────────

export interface WindowSummary {
  /** matrix[o][d], riders per day within the window. */
  matrix: number[][];
  /** Riders aboard on each link within the window, by direction. */
  loads: Record<Dir, number[]>;
  boardings: Record<Dir, number>;
  total: number;
}

/** Everything in buckets starting in [fromMin, toMin). */
export function summariseWindow(demand: DemandAnalysis, fromMin: number, toMin: number): WindowSummary {
  const n = demand.stops.length;
  const matrix = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  const loads: Record<Dir, number[]> = {
    upward: new Array<number>(Math.max(0, n - 1)).fill(0),
    downward: new Array<number>(Math.max(0, n - 1)).fill(0),
  };
  const boardings: Record<Dir, number> = { upward: 0, downward: 0 };

  for (const b of demand.buckets) {
    if (b.start_min < fromMin || b.start_min >= toMin) continue;
    for (let o = 0; o < n; o++) {
      const row = b.od[o];
      if (!row) continue;
      for (let d = 0; d < n; d++) matrix[o][d] += row[d] ?? 0;
    }
    for (const dir of DIRS) {
      boardings[dir] += b[dir].boardings;
      b[dir].link_loads.forEach((v, k) => {
        loads[dir][k] += v;
      });
    }
  }
  return { matrix, loads, boardings, total: boardings.upward + boardings.downward };
}

/** The window covering every bucket with demand. */
export function fullWindow(demand: DemandAnalysis): [number, number] {
  if (demand.buckets.length === 0) return [0, 1440];
  const first = demand.buckets[0].start_min;
  const last = demand.buckets[demand.buckets.length - 1].start_min;
  return [first, last + demand.bucket_minutes];
}

// ── headline figures ───────────────────────────────────────────────────────

export interface PeakBucket {
  startMin: number;
  dir: Dir;
  load: number;
  link: number;
}

/** The single busiest bucket and direction of the day. */
export function peakBucket(buckets: DemandBucket[]): PeakBucket | null {
  let best: PeakBucket | null = null;
  for (const b of buckets) {
    for (const dir of DIRS) {
      if (!best || b[dir].peak_load > best.load) {
        best = { startMin: b.start_min, dir, load: b[dir].peak_load, link: b[dir].peak_link };
      }
    }
  }
  return best && best.load > 0 ? best : null;
}

export function busiestPair(matrix: number[][]): { o: number; d: number; riders: number } | null {
  let best: { o: number; d: number; riders: number } | null = null;
  matrix.forEach((row, o) =>
    row.forEach((riders, d) => {
      if (o !== d && (!best || riders > best.riders)) best = { o, d, riders };
    })
  );
  return best && (best as { riders: number }).riders > 0 ? best : null;
}

// ── colour ─────────────────────────────────────────────────────────────────

/**
 * One-hue ramps for the matrix, light to dark. Blue is the dataviz palette's
 * documented sequential ramp; orange is the same thirteen lightness steps in
 * the second categorical hue, so a cell's depth means the same in either
 * triangle. Steps 6 and 7 are skipped: they are the mid-lightness band where
 * neither white nor dark ink clears 4.5:1 for the figure printed on the cell.
 * Every step kept gives the figure at least 5.3:1.
 */
const HEAT: Record<Dir, string[]> = {
  upward: [
    "#cde2fb", "#b7d3f6", "#9ec5f4", "#86b6ef", "#6da7ec", "#5598e7",
    "#256abf", "#1c5cab", "#184f95", "#104281", "#0d366b",
  ],
  downward: [
    "#fbd7ca", "#f5c4b2", "#f2b098", "#eb9c7f", "#e68764", "#df7249",
    "#b44005", "#9f3600", "#8a2e00", "#752600", "#611e00",
  ],
};
/** From this step on the fill is dark enough to need white ink. */
const HEAT_FIRST_DARK = 6;

export const HEAT_STEPS = HEAT.upward.length;

export interface HeatCell {
  fill: string;
  /** True when the figure on the cell should be white rather than ink. */
  light: boolean;
}

/**
 * Colour for a value on a square-root scale: flows here span three orders of
 * magnitude, and a linear ramp would leave every cell but the terminal's
 * looking empty.
 */
export function heatCell(value: number, max: number, dir: Dir): HeatCell | null {
  if (!(value > 0) || !(max > 0)) return null;
  const t = Math.sqrt(Math.min(1, value / max));
  const step = Math.min(HEAT_STEPS - 1, Math.max(0, Math.round(t * (HEAT_STEPS - 1))));
  return { fill: HEAT[dir][step], light: step >= HEAT_FIRST_DARK };
}

/**
 * Colour for a share of something — a bus's load against its capacity — on
 * the direction's ramp, linear: 0 is the lightest step, 1 the darkest.
 */
export function rampColour(share: number, dir: Dir): HeatCell {
  const t = Number.isFinite(share) ? Math.min(1, Math.max(0, share)) : 0;
  const step = Math.min(HEAT_STEPS - 1, Math.max(0, Math.round(t * (HEAT_STEPS - 1))));
  return { fill: HEAT[dir][step], light: step >= HEAT_FIRST_DARK };
}

/** The value at which each ramp step begins, for the scale legend. */
export function heatStops(max: number, dir: Dir): Array<{ fill: string; from: number }> {
  return HEAT[dir].map((fill, i) => {
    const t = Math.max(0, (i - 0.5) / (HEAT_STEPS - 1));
    return { fill, from: i === 0 ? 0 : t * t * max };
  });
}

// ── axes ───────────────────────────────────────────────────────────────────

/** A clean upper bound and tick step for a value axis starting at zero. */
export function niceScale(max: number, targetTicks = 4): { max: number; step: number } {
  if (!(max > 0)) return { max: 1, step: 1 };
  const raw = max / targetTicks;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / pow;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * pow;
  return { max: Math.ceil(max / step) * step, step };
}
